import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo
} from 'react'
import deepEqual from 'deep-equal'

import { APIRequestContext } from '../../../../wrappers/APIRequestContext'
import withConfig from '../../../../wrappers/withConfig'
import actions from '../../../../../utils/submissions/actions'
import toast from '../../../../elem/Toast'

import { getQueryStringFromParams } from '../../../../../utils/params'
import { DataContext } from '../../DataContext'
import { debounce } from 'debounce'
import getFilterParamsFromForm from '../../../../../utils/form/getFilterParamsFromForm'

const FacilityAssignmentMITContext = createContext(null)

const FacilityAssignmentContextProvider = ({ config, children }) => {
    const [loadingPossibleFacilities, setLoadingPossibleFacilities] = useState(false)
    const [loadingAssignedFacilities, setLoadingAssignedFacilities] = useState(false)
    const [loadingConfig, setLoadingConfig] = useState(true)
    const { authenticatedFetch } = useContext(APIRequestContext)
    const { submissionState } = useContext(DataContext)
    const [projectFacilityFilterConfig, setProjectFacilityFilterConfig] = useState(null)
    const [projectFacilityListConfig, setProjectFacilityListConfig] = useState(null)
    const [assignedFacilityData, setAssignedFacilityData] = useState([])
    const [assignedFacilityMetadata, setAssignedFacilityMetadata] = useState([])
    const [possibleFacilityData, setPossibleFacilityData] = useState([])
    const [possibleFacilityMetadata, setPossibleFacilityMetadata] = useState({})
    const [highlightedPossibleFacilities, setHighlightedPossibleFacilities] = useState([])
    const [highlightedAssignedFacilities, setHighlightedAssignedFacilities] = useState([])
    const [filterResultsCount, setFilterResultsCount] = useState()
    const [filterResultsLoading, setFilterResultsLoading] = useState()
    const agentId = useMemo(() => submissionState && submissionState.Agent && submissionState.Agent.Pkey, [submissionState])
    const facilityAccessor = `FacilityAgentMIT`
    const [params, setParams] = useState({
        'possibleMITFacilities': {
            Page: 1,
            PageSize: 50
        },
        'assignedMITFacilities':
        {
            Page: 1,
            PageSize: 50
        }
    })
    const possibleFacilityParams = useRef({})
    const assignedFacilityParams = useRef({})
    const assignedFacilityState = useRef([])
    const [possibleFacilityAbortController, setPossibleFacilityAbortController] = useState(new AbortController())
    const [assignedFacilityAbortController, setAssignedFacilityAbortController] = useState(new AbortController())
    const [filterResultsAbortController, setFilterResultsAbortController] = useState(new AbortController())
    const { API_URL, ID_COLUMN } = config
    const assignedFacilityIds = useMemo(() => assignedFacilityData.map(x => x[ID_COLUMN]).join(','), [assignedFacilityData])
    
    const updateProjectFacilityConfig = (data) => {
        setProjectFacilityListConfig(data.list)
        setProjectFacilityFilterConfig(data.list.filter(x => x.DisplayInFilter))
        setLoadingConfig(false)
    }

    useEffect(() => {
        setLoadingConfig(true)
        actions.getFacilityAssignmentConfig(
            'mit',
            authenticatedFetch,
            API_URL,
            updateProjectFacilityConfig
        )
    }, [])

    const fetchAssignedFacilities = useCallback(() => {
        setLoadingAssignedFacilities(true)
        const abort = new AbortController()
        setAssignedFacilityAbortController(abort)
        const paramString = getQueryStringFromParams(params)
        authenticatedFetch(
            `${API_URL}/upload/mit/assignedMITFacilities?${paramString}&uploadID=${submissionState.UploadId}`,
            { signal: abort.signal }
        )
            .then(async response => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then(response => {
                setAssignedFacilityData(response.data)
                setAssignedFacilityMetadata(response.meta)
                setLoadingAssignedFacilities(false)
            })
            .catch(e => {
                if (e.name !== 'AbortError') {
                    toast({
                        level: 'error',
                        message:
                            'Assigned Sampling Points List: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                    setLoadingAssignedFacilities(false)
                }
            })
    }, [assignedFacilityData, submissionState])

    const fetchPossibleFacilities = useCallback(params => {
        // const assignedFacilityIds = assignedFacilityData.map(x => x[ID_COLUMN]).join(',')
        setLoadingPossibleFacilities(true)
        const abort = new AbortController()
        setPossibleFacilityAbortController(abort)
        const paramString = getQueryStringFromParams(params)
        authenticatedFetch(`${API_URL}/upload/mit/mitFacilities?${paramString}&assignedFacilityIds=${assignedFacilityIds}&agentId=${agentId}`, { signal: abort.signal })
            .then(async response => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then(response => {
                setPossibleFacilityData(response.data)
                setPossibleFacilityMetadata(response.meta)
                setLoadingPossibleFacilities(false)
            })
            .catch(e => {
                if (e.name !== 'AbortError') {
                    toast({
                        level: 'error',
                        message:
                            'Possible Sampling Points List: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                    setLoadingPossibleFacilities(false)
                }
            })
    }, [assignedFacilityIds, ID_COLUMN, agentId])

    // fetch possible facility data on parameter or assigned facility data changes
    useEffect(() => {
        const hasNewAssignedFacilities = !deepEqual(assignedFacilityData.map(x => x[ID_COLUMN]).sort(), assignedFacilityState.current.sort())
        if (params && params.possibleMITFacilities) {
            const associatedParams = params.possibleMITFacilities
            if (submissionState && submissionState.UploadId && submissionState.Agent) {
                if (!deepEqual(possibleFacilityParams.current, associatedParams) || hasNewAssignedFacilities) {
                    possibleFacilityAbortController.abort()
                    fetchPossibleFacilities(params)
                    possibleFacilityParams.current = associatedParams
                }
            }
        }
    }, [params, assignedFacilityData, possibleFacilityAbortController, submissionState])

    const fetchResultsDebounce = useCallback(debounce((e, formData) => {
        // query for the updated count
        const newParams = {
            ...params, 
            possibleMITFacilities: {
                ...getFilterParamsFromForm(formData)
            }
        }
        const abort = new AbortController()
        setFilterResultsAbortController(abort)
        const paramString = getQueryStringFromParams(newParams)
        authenticatedFetch(`${API_URL}/upload/mit/mitFacilities/count?${paramString}&assignedFacilityIds=${assignedFacilityIds}&agentId=${agentId}`, {signal: abort.signal})
            .then(async response => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then(response => {
                const { meta } = response
                const { count } = meta
                setFilterResultsCount(count)
                setFilterResultsLoading(false)
            })
            .catch(e => {
                if (e.name !== 'AbortError') {
                    toast({
                        level: 'error',
                        message:
                            'Sample List: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                    setFilterResultsLoading(false)
                }
            })
    }, 3000, false), [assignedFacilityIds, agentId])

    const fetchResultsCount = useCallback((e, formData) => {
        filterResultsAbortController.abort()
        setFilterResultsLoading(true)
        fetchResultsDebounce.clear()
        if (submissionState && submissionState.UploadId && submissionState.Agent) {
            fetchResultsDebounce(e, formData)            
        }
    }, [filterResultsAbortController, submissionState, assignedFacilityIds])

    const updateParams = (newParams, field) => {
        setParams(previousParams => ({
            ...previousParams,
            [field]: {
                ...newParams
            }
        }))
    } 

    useEffect(() => {
        if (params && params.assignedMITFacilities) {
            const associatedParams = params.assignedMITFacilities
            if (submissionState && submissionState.UploadId && submissionState.Agent) {
                if (!deepEqual(assignedFacilityParams.current, associatedParams)) {
                    assignedFacilityAbortController.abort()
                    fetchAssignedFacilities(params)
                    assignedFacilityParams.current = associatedParams
                }
            }
        }
    }, [submissionState, params])

    useEffect(() => {
        const initialAssignedFacilities = submissionState[facilityAccessor]
        ? submissionState[facilityAccessor]
        : []

        if (!deepEqual(initialAssignedFacilities.map(x => x[ID_COLUMN]).sort(), assignedFacilityState.current.sort())){
            assignedFacilityAbortController.abort()
            fetchAssignedFacilities()
        }
    }, [submissionState, ID_COLUMN])

    useEffect(() => {
        const updatedFacilityState = assignedFacilityData.map(x => x[ID_COLUMN])
        assignedFacilityState.current = updatedFacilityState
    }, [assignedFacilityData])

    return (
        <FacilityAssignmentMITContext.Provider
            value={{
                loadingPossibleFacilities,
                loadingAssignedFacilities,
                loadingConfig,
                projectFacilityFilterConfig,
                projectFacilityListConfig,
                params,
                setParams: updateParams,
                possibleFacilityData,
                possibleFacilityMetadata,
                setPossibleFacilityData,
                assignedFacilityData,
                assignedFacilityMetadata,
                setAssignedFacilityData,
                facilityAccessor,
                highlightedAssignedFacilities,
                setHighlightedAssignedFacilities,
                highlightedPossibleFacilities,
                setHighlightedPossibleFacilities,
                fetchResultsCount,
                filterResultsCount,
                filterResultsLoading,
            }}
        >
            {children}
        </FacilityAssignmentMITContext.Provider>
    )
}

export { FacilityAssignmentMITContext }
export default withConfig(FacilityAssignmentContextProvider)
